import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CalendarDays, ExternalLink, HelpCircle, Inbox, LayoutDashboard, LogOut, Megaphone, Newspaper, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, clearToken, getToken } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/blog", label: "Blog posts", icon: Newspaper },
  { to: "/admin/timetable", label: "Timetable", icon: CalendarDays },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/leads", label: "Leads", icon: Megaphone },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/help", label: "Help", icon: HelpCircle },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  if (checking || !user) {
    return (
      <div className="min-h-screen bg-background p-8">
        <Skeleton className="h-14 w-full rounded-[4px]" />
        <Skeleton className="h-64 w-full rounded-[4px] mt-6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <Helmet>
        <title>Studio Admin | Kerry's</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/admin" className="font-display text-xl font-bold shrink-0" data-testid="admin-logo-link">
            Kerry<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;s</span> <span className="text-muted-foreground font-sans text-sm font-medium">Admin</span>
          </Link>
          <nav className="flex items-center gap-1 overflow-x-auto">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3.5 py-2 rounded-[4px] text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-muted"
                  }`
                }
              >
                <item.icon className="h-4 w-4" /> {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Button asChild variant="ghost" size="sm" className="rounded-[4px] hidden sm:inline-flex" data-testid="admin-view-site-button">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" /> View site
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-[4px]" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-1" /> Log out
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
}
